import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { api } from '@/shared/api/axios';

// Super Admin only (the page renders it for them alone, and the endpoint
// refuses anyone else). Contacts are shared by every employee; this is where
// the Super Admin sees who added a contact, who it belonged to before, and who
// is working on it now.

interface TrackedUser { name: string; username: string; department: string; active: boolean }
interface Worker {
  userId: string;
  user: TrackedUser | null;
  pipelineRecords: number;
  documents: number;
  contactEdits: number;
  lastActivityAt: string | null;
}
interface ContactTracking {
  currentOwner: TrackedUser | null;
  addedAt: string | null;
  previousOwnership: {
    handovers: { at: string; details: string; decidedByUser: TrackedUser | null }[];
    lastRequest: { status: string; toUser: TrackedUser | null; requestedBy: TrackedUser | null; requestedAt: string | null; decidedAt: string | null } | null;
  };
  workingOn: Worker[];
  recentDocuments: { number: string; type: string; status: string; by: TrackedUser | null; at: string }[];
}

const fmt = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const nameOf = (u: TrackedUser | null) => (u ? `${u.name}${u.active ? '' : ' (inactive)'}` : 'Unknown');
const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function ContactTrackingPanel({ contactId }: { contactId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['contacts', 'tracking', contactId],
    queryFn: async () => (await api.get<ContactTracking>(`/api/contacts-identity/${contactId}/tracking`)).data,
  });

  return (
    <section className="rounded-2xl border border-outline bg-surface p-5 shadow-xs">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 text-primary" />
        <h3 className="text-[15px] font-semibold text-on-surface">Contact tracking</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-data-xs font-medium text-slate-600">Super Admin only</span>
      </div>

      {isLoading ? (
        <p className="mt-3 text-body-sm text-on-surface-variant">Loading…</p>
      ) : isError || !data ? (
        <p className="mt-3 text-body-sm text-on-surface-variant">Tracking could not be loaded.</p>
      ) : (
        <div className="mt-4 grid gap-5 lg:grid-cols-3">
          <div className="space-y-3">
            <div>
              <p className="text-body-xs font-medium text-on-surface-variant">Contact holder</p>
              <p className="text-body-sm font-semibold text-on-surface">{nameOf(data.currentOwner)}</p>
              <p className="text-body-xs text-on-surface-variant">Added {fmt(data.addedAt)}</p>
            </div>
            <div>
              <p className="text-body-xs font-medium text-on-surface-variant">Belonged to before</p>
              {data.previousOwnership.lastRequest?.status === 'approved' || data.previousOwnership.handovers.length ? (
                <ul className="mt-1 space-y-1 text-body-xs text-on-surface">
                  {data.previousOwnership.lastRequest?.status === 'approved' && (
                    <li>
                      Handed to <span className="font-semibold">{nameOf(data.previousOwnership.lastRequest.toUser)}</span>
                      {' '}on {fmt(data.previousOwnership.lastRequest.decidedAt)} (requested by {nameOf(data.previousOwnership.lastRequest.requestedBy)})
                    </li>
                  )}
                  {data.previousOwnership.handovers.map((h, i) => (
                    <li key={i} className="text-on-surface-variant">{fmt(h.at)} — {h.details}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-body-sm text-on-surface-variant">No earlier holder — never handed over.</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <p className="text-body-xs font-medium text-on-surface-variant">Working on this contact</p>
            {data.workingOn.length === 0 ? (
              <p className="mt-1 text-body-sm text-on-surface-variant">Nobody has raised an enquiry or document for it yet.</p>
            ) : (
              <table className="mt-2 w-full text-body-xs">
                <thead>
                  <tr className="border-b border-outline text-left text-on-surface-variant">
                    <th className="py-1.5 pr-3 font-medium">Employee</th>
                    <th className="py-1.5 pr-3 font-medium">Work</th>
                    <th className="py-1.5 font-medium text-right">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.workingOn.map((w) => (
                    <tr key={w.userId} className="border-b border-outline/60 last:border-0">
                      <td className="py-1.5 pr-3 font-semibold text-on-surface">
                        {nameOf(w.user)}
                        {w.user?.department && <span className="ml-1 font-normal capitalize text-on-surface-variant">· {w.user.department}</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-on-surface-variant">
                        {[
                          w.pipelineRecords ? count(w.pipelineRecords, 'enquiry', 'enquiries') : '',
                          w.documents ? count(w.documents, 'document', 'documents') : '',
                          w.contactEdits ? count(w.contactEdits, 'contact edit', 'contact edits') : '',
                        ].filter(Boolean).join(' · ')}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-on-surface-variant">{fmt(w.lastActivityAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {data.recentDocuments.length > 0 && (
              <p className="mt-3 text-body-xs text-on-surface-variant">
                Latest: {data.recentDocuments.slice(0, 3).map((d) => `${d.type} ${d.number} by ${nameOf(d.by)}`).join(' · ')}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
